import { useCallback, useEffect, useRef, useState } from 'react';
import { LiveConnectionMode, LiveRoomState } from '../types/liveQuiz';
import { getRoomState, getStreamUrl, sendHeartbeat } from '../services/liveQuizService';
import { getToken } from '../utils/jwtUtils';
import { anchor as anchorServerClock } from '../utils/serverClock';

/** How often to poll when the stream is unavailable. */
const POLL_INTERVAL_MS = 1500;

/** How long to wait for the stream before giving up and polling. */
const STREAM_CONNECT_TIMEOUT_MS = 5000;

/** Presence heartbeat cadence. */
const HEARTBEAT_INTERVAL_MS = 20_000;

export interface UseLiveQuizRoomResult {
  state: LiveRoomState | null;
  mode: LiveConnectionMode;
  error: string | null;
  /**
   * Milliseconds left in the current phase, corrected for clock skew between
   * this device and the server. Null when the phase has no deadline.
   */
  remainingMs: number | null;
  refresh: () => Promise<void>;
}

/**
 * Subscribes to a live quiz room.
 *
 * Two things make this reliable rather than merely functional:
 *
 * 1. **Clock skew correction.** The server stamps every payload with its own
 *    time. We diff that against the local clock once per payload and apply the
 *    offset to every deadline, so a device with a badly wrong clock still shows
 *    the same countdown as everyone else in the room.
 *
 * 2. **The stream carries transitions, not ticks.** Payloads contain absolute
 *    deadlines, and the countdown is computed locally from them. A dropped
 *    connection therefore loses nothing — the next payload re-syncs exactly, and
 *    the timer keeps running correctly in the meantime.
 *
 * Falls back to polling if EventSource cannot connect, which also covers
 * networks that buffer or block text/event-stream.
 */
export function useLiveQuizRoom(roomId: number | null): UseLiveQuizRoomResult {
  const [state, setState] = useState<LiveRoomState | null>(null);
  const [mode, setMode] = useState<LiveConnectionMode>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());

  /**
   * Server and local time captured at the same instant, from the most recent
   * payload.
   *
   * Storing the pair rather than a single offset is deliberate. The local clock
   * is used only to measure *elapsed* time since the anchor, never as an
   * absolute reading — so a device whose clock is wrong, or which jumps while
   * the page is open, still counts down correctly, and the next payload
   * re-anchors it.
   */
  const anchorRef = useRef<{ serverMs: number; localMs: number } | null>(null);
  const sourceRef = useRef<EventSource | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const applyPayload = useCallback((payload: LiveRoomState) => {
    // Re-anchor on every payload rather than once at connect: a laptop waking
    // from sleep can shift its clock mid-quiz.
    const localMs = Date.now();
    anchorRef.current = { serverMs: new Date(payload.serverNow).getTime(), localMs };
    // Also feed the shared clock, so countdowns elsewhere on the page (the
    // lobby's "starts in", the dashboard banner) benefit from this room's
    // fresher, per-payload anchor without needing their own transport.
    anchorServerClock(payload.serverNow);
    setState(payload);
    // Re-sample immediately so the countdown reflects the new anchor without
    // waiting for the next tick.
    setNow(localMs);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    if (roomId === null) {
      return;
    }
    try {
      applyPayload(await getRoomState(roomId));
    } catch (err) {
      setError('Lost contact with the quiz. Retrying…');
    }
  }, [roomId, applyPayload]);

  const startPolling = useCallback(() => {
    if (pollRef.current || roomId === null) {
      return;
    }
    setMode('polling');
    void refresh();
    pollRef.current = setInterval(() => void refresh(), POLL_INTERVAL_MS);
  }, [roomId, refresh]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Subscribe: stream first, polling as fallback.
  useEffect(() => {
    if (roomId === null) {
      return undefined;
    }

    const token = getToken();
    if (!token) {
      setError('You need to be signed in to join a live quiz.');
      setMode('closed');
      return undefined;
    }

    let cancelled = false;
    setMode('connecting');

    // Seed immediately so the page renders without waiting for the first event,
    // which could be a whole question away.
    void refresh();

    const source = new EventSource(getStreamUrl(roomId, token));
    sourceRef.current = source;

    // If the stream has not opened by now, assume it never will.
    const connectTimeout = setTimeout(() => {
      if (!cancelled && source.readyState !== EventSource.OPEN) {
        source.close();
        startPolling();
      }
    }, STREAM_CONNECT_TIMEOUT_MS);

    source.onopen = () => {
      if (cancelled) return;
      clearTimeout(connectTimeout);
      stopPolling();
      setMode('stream');
    };

    source.addEventListener('state', event => {
      if (cancelled) return;
      try {
        applyPayload(JSON.parse((event as MessageEvent).data));
      } catch {
        setError('Received an unreadable update.');
      }
    });

    source.addEventListener('aborted', () => {
      if (cancelled) return;
      setMode('closed');
      source.close();
      void refresh();
    });

    source.onerror = () => {
      if (cancelled) return;
      // EventSource retries on its own, but a network that blocks event-stream
      // will never succeed — poll alongside so the student is never stuck.
      startPolling();
    };

    return () => {
      cancelled = true;
      clearTimeout(connectTimeout);
      source.close();
      sourceRef.current = null;
      stopPolling();
    };
  }, [roomId, refresh, applyPayload, startPolling, stopPolling]);

  // Local countdown tick. Independent of the transport, so the timer stays
  // smooth even while the connection is down.
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(interval);
  }, []);

  // Presence heartbeat. Also nudges the room forward when nobody else is making
  // requests, which matters because nothing else fires on a schedule.
  useEffect(() => {
    if (roomId === null) {
      return undefined;
    }

    const interval = setInterval(() => {
      void sendHeartbeat(roomId).catch(() => undefined);
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [roomId]);

  // Stop polling once the quiz is over.
  useEffect(() => {
    if (state && (state.status === 'finished' || state.status === 'abandoned')) {
      stopPolling();
    }
  }, [state, stopPolling]);

  // Server time now = the server's time at the anchor, plus however long has
  // elapsed locally since. Only the difference of two local readings is used,
  // so an incorrect device clock cancels out.
  const anchor = anchorRef.current;
  const remainingMs =
    state?.phaseEndsAt && anchor
      ? Math.max(
          0,
          new Date(state.phaseEndsAt).getTime() - (anchor.serverMs + (now - anchor.localMs))
        )
      : null;

  return { state, mode, error, remainingMs, refresh };
}
